/**
 * Shared report display/filter configs.
 */
import {
  FileEdit,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileCheck,
  User,
  UsersRound,
  Info,
  Users,
  GraduationCap,
  Heart,
  Handshake,
  UserCog,
  Briefcase,
  Shield,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  SESSION_REPORT_FEELING_VALUES,
  type ReportStatus,
  type ReportFilters,
  type SessionReportFeeling,
} from '@/types/inperson/report.types';
import type { ReportTab } from '@/types/remote/report.types';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import { getRoleLabel } from '@/utils/role-label-utils';

export {
  DATE_RANGE_PERIOD_OPTIONS,
  DATE_RANGE_PERIOD_OPTIONS_FULL,
  REMOTE_SUBJECT_CONFIG,
  REMOTE_SUBJECT_OPTIONS,
  SESSION_REMOTE_TYPE_CONFIG,
  SESSION_TYPE_CONFIG,
  SESSION_TYPE_FILTER_KEYS,
  STUDENT_ABSENCE_REASONS,
  TUTOR_ABSENCE_REASONS,
} from '@/constants/shared-catalog';

/** In-person report list tabs. */
export type ReportInPersonTab = 'tutoring' | 'information-meeting';

/** Labels for in-person report tabs. */
export const REPORT_IN_PERSON_TAB_LABELS: Record<ReportInPersonTab, string> = {
  tutoring: 'Tutorat',
  'information-meeting': "Réunion d'information",
};

/** Sort key: tutor, then volunteer, then student. */
export const REPORT_PARTICIPANT_ROLE_ORDER: Record<'tutor' | 'volunteer' | 'student', number> = {
  tutor: 0,
  volunteer: 1,
  student: 2,
};

/** Session types shown under each in-person report tab. */
export const REPORT_IN_PERSON_TAB_TO_TYPES: Record<ReportInPersonTab, string[]> = {
  tutoring: [
    'individual_tutoring',
    'homework_help',
    'group_tutoring',
    'workshop',
    'whole_class',
    'quick_help_desk',
  ],
  'information-meeting': ['information_meeting_in_person'],
};

/** Modal copy when picking a remote report type. */
export const REPORT_REMOTE_SELECT_TYPE = {
  title: 'Créer un compte rendu',
  subtitle: 'Sélectionnez le type de compte rendu à créer.',
} as const;

/** Modal copy when picking an in-person report type. */
export const REPORT_IN_PERSON_SELECT_TYPE = {
  title: 'Créer un compte rendu',
  subtitle: 'Sélectionnez le type de compte rendu à créer.',
} as const;

/** Create-flow card for a remote report type. */
export interface ReportRemoteCreateTypeOption {
  id: ReportTab;
  title: string;
  description: string;
  icon: LucideIcon;
  accentVar: string;
}

/** Create-flow card for an in-person report type. */
export interface ReportInPersonCreateTypeOption {
  id: ReportInPersonTab;
  title: string;
  description: string;
  icon: LucideIcon;
  accentVar: string;
}

/** Create-flow cards for remote report types. */
export const REPORT_REMOTE_CREATE_TYPE_OPTIONS: ReportRemoteCreateTypeOption[] = [
  {
    id: 'tutoring',
    title: 'Tutorat',
    description: 'Compte rendu de séance de tutorat individuel.',
    icon: User,
    accentVar: 'var(--color-1-nuance-1)',
  },
  {
    id: 'information-meeting',
    title: "Réunion d'information",
    description: "Synthèse de réunion d'information avec tuteurs.",
    icon: Info,
    accentVar: 'var(--color-2-nuance-1)',
  },
  {
    id: 'welcome-meeting',
    title: "Réunion d'accueil",
    description: "Synthèse de réunion d'accueil avec parent et élève.",
    icon: Users,
    accentVar: 'var(--color-3-nuance-1)',
  },
  {
    id: 'field-event',
    title: 'Événement terrain',
    description: "Compte rendu d'événement de recrutement.",
    icon: UsersRound,
    accentVar: 'var(--color-4-nuance-1)',
  },
];

/** Create-flow cards for in-person report types. */
export const REPORT_IN_PERSON_CREATE_TYPE_OPTIONS: ReportInPersonCreateTypeOption[] = [
  {
    id: 'tutoring',
    title: 'Tutorat',
    description: 'Compte rendu de séance de tutorat.',
    icon: User,
    accentVar: 'var(--color-1-nuance-1)',
  },
  {
    id: 'information-meeting',
    title: "Réunion d'information",
    description: "Synthèse de réunion d'information.",
    icon: Info,
    accentVar: 'var(--color-2-nuance-1)',
  },
];

/** Remote create cards allowed by the current RBAC tab ids. */
export const getVisibleRemoteReportCreateTypeOptions = (
  allowedTabIds?: ReportTab[],
): ReportRemoteCreateTypeOption[] => {
  if (allowedTabIds && allowedTabIds.length > 0) {
    return REPORT_REMOTE_CREATE_TYPE_OPTIONS.filter((opt) => allowedTabIds.includes(opt.id));
  }
  return REPORT_REMOTE_CREATE_TYPE_OPTIONS;
};

/** In-person create cards allowed by the current RBAC tab ids. */
export const getVisibleInPersonReportCreateTypeOptions = (
  allowedTabIds?: ReportInPersonTab[],
): ReportInPersonCreateTypeOption[] => {
  if (allowedTabIds && allowedTabIds.length > 0) {
    return REPORT_IN_PERSON_CREATE_TYPE_OPTIONS.filter((opt) => allowedTabIds.includes(opt.id));
  }
  return REPORT_IN_PERSON_CREATE_TYPE_OPTIONS;
};

/** Sequential tab colors for in-person reports. */
export const REPORT_IN_PERSON_TAB_COLORS: Record<ReportInPersonTab, string> = {
  tutoring: 'var(--color-1-nuance-1)',
  'information-meeting': 'var(--color-2-nuance-1)',
};

/** API session type for in-person information meetings. */
export const REPORT_IN_PERSON_SESSION_TYPE = {
  informationMeeting: 'information_meeting_in_person',
} as const;

/** React Query keys for in-person report forms and lists. */
export const REPORT_IN_PERSON_QUERY_KEYS = {
  report: (sessionId: string, reportId: string) => ['report', sessionId, reportId] as const,
  session: (sessionId: string) => ['session', sessionId] as const,
  sessionDetail: (sessionId: string) => ['sessions', sessionId] as const,
  list: ['reports'] as const,
  subjects: ['subjects', 'edit-report'] as const,
  studentFeedbackCatalog: ['student-feedback-catalog'] as const,
};

/** Default subjects list query for the edit-report form. */
export const REPORT_IN_PERSON_SUBJECTS_QUERY = { page: 1, pageSize: 10 } as const;

/** Rating values used by the in-person report filter. */
export const REPORT_RATING_FILTER_VALUES = [1, 2, 3, 4, 5] as const;

/** Badge + icon config for an in-person report status. */
export interface ReportStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Badge + icon per in-person report status. */
export const REPORT_STATUS_CONFIG: Record<ReportStatus, ReportStatusConfig> = {
  to_complete: {
    label: 'À saisir',
    icon: FileEdit,
    badgeVariant: 'on-hold',
    badgeColor: 'rgb(var(--status-pending))',
  },
  draft: {
    label: 'Brouillon',
    icon: FileEdit,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  submitted: {
    label: 'Soumis',
    icon: Send,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  validated: {
    label: 'Validé',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  overdue: {
    label: 'En retard',
    icon: AlertCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Empty in-person report list filters. */
export const DEFAULT_REPORT_FILTERS: ReportFilters = {
  statuses: [],
  ratings: [],
  dateRange: 'month',
  customDateFrom: undefined,
  customDateTo: undefined,
  sessionTypes: [],
  establishmentIds: [],
  search: undefined,
};

/** Filter group IDs for the in-person reports configure panel. */
export type FilterGroupId =
  | 'filter-search'
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-status'
  | 'filter-rating'
  | 'filter-type-session'
  | 'filter-establishment';

/** Filter group ids shown in the in-person configure panel. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-search',
  'filter-period',
  'filter-date-range',
  'filter-status',
  'filter-rating',
  'filter-type-session',
  'filter-establishment',
];

/** Human-readable label per filter group, for the configure panel. */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-search': 'Recherche participant',
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-status': 'Statut',
  'filter-rating': 'Note',
  'filter-type-session': 'Type de séance',
  'filter-establishment': 'Établissement',
};

/** Badge + icon config for a remote report status. */
export interface RemoteReportStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: 'ghost' | 'outlined' | 'solid' | 'active' | 'disabled' | 'on-hold';
  badgeColor: string;
}

/** Badge + icon per remote tutoring report status. */
export const REMOTE_REPORT_TUTORING_STATUS_CONFIG: Record<string, RemoteReportStatusConfig> = {
  a_saisir: {
    label: 'À saisir',
    icon: FileEdit,
    badgeVariant: 'on-hold',
    badgeColor: 'rgb(var(--status-pending))',
  },
  saisi: {
    label: 'Saisi',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  valide: {
    label: 'Validé',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
};

/** Badge + icon per remote synthesis report status. */
export const REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG: Record<string, RemoteReportStatusConfig> = {
  incomplet: {
    label: 'Incomplet',
    icon: AlertCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
  complet: {
    label: 'Complet',
    icon: FileCheck,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
};

/** Merged remote report statuses (tutoring + synthesis). */
export const REMOTE_REPORT_STATUS_CONFIG: Record<string, RemoteReportStatusConfig> = {
  ...REMOTE_REPORT_TUTORING_STATUS_CONFIG,
  ...REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG,
};

/** Maps activitesRealisees keys → human-readable labels. */
export const ACTIVITES_REALISEES_LABELS: Record<string, string> = {
  exercices: 'Exercices',
  cours: 'Cours',
  devoirs: 'Devoirs',
  rappel_de_cours: 'Rappel de cours',
  quiz: 'Quiz',
  presentation: 'Présentation',
  expose: 'Exposé',
};

/** Select options built from activity labels. */
export const ACTIVITES_REALISEES_SELECT_OPTIONS = Object.entries(ACTIVITES_REALISEES_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Badge + icon per remote scoring status. */
export const REMOTE_REPORT_SCORING_STATUS_CONFIG: Record<string, RemoteReportStatusConfig> = {
  incomplet: {
    label: 'Incomplet',
    icon: AlertCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  complet: {
    label: 'Complet',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
};

/** English names for frontend checks. Values stay the API / RBAC tab ids. */
export const REPORT_REMOTE_TAB = {
  all: 'all',
  tutoring: 'tutoring',
  information: 'information-meeting',
  welcome: 'welcome-meeting',
  fieldEvent: 'field-event',
} as const satisfies Record<string, ReportTab>;

/** Labels for remote report tabs. */
export const REMOTE_REPORT_TAB_LABELS: Record<ReportTab, string> = {
  all: 'Tout',
  tutoring: 'Tutorat',
  'information-meeting': "Réunion d'information",
  'welcome-meeting': "Réunion d'accueil",
  'field-event': 'Événement terrain',
};

/** Sequential tab colors — shared between dashboard section and management page. */
export const REMOTE_REPORT_TAB_COLORS: Record<ReportTab, string> = {
  all: 'var(--color-5-nuance-1)',
  tutoring: 'var(--color-1-nuance-1)',
  'information-meeting': 'var(--color-2-nuance-1)',
  'welcome-meeting': 'var(--color-3-nuance-1)',
  'field-event': 'var(--color-4-nuance-1)',
};

/**
 * Session types per tab. These mirror the remote KPI card families so a tab
 * filter always totals the matching dashboard card. The API additionally restricts every row to
 * `session_format = 'remote'`, so in-person-only types never surface here.
 */
export const REMOTE_REPORT_TAB_SESSION_TYPES: Record<ReportTab, string[]> = {
  all: [],
  tutoring: ['individual_tutoring', 'homework_help', 'group_tutoring'],
  'information-meeting': ['information_meeting'],
  'welcome-meeting': ['welcome_meeting'],
  'field-event': ['workshop'],
};

/** Per-report-type RBAC flags for list-page edit, validate, and delete. */
export interface PerTypeReportPageActions {
  canDeleteRow?: boolean;
  canEditRowTutoring?: boolean;
  canEditRowInformationMeeting?: boolean;
  canEditRowInformation?: boolean; // In-person alias
  canEditRowWelcome?: boolean;
  canEditRowFieldEvent?: boolean;
  canValidateTutoring?: boolean;
  canValidateInformationMeeting?: boolean;
  canValidateInformation?: boolean; // In-person alias
  canValidateWelcome?: boolean;
  canValidateFieldEvent?: boolean;
}

/** Badge + icon per remote session type on a report. */
export const REMOTE_REPORT_SESSION_TYPE_CONFIG: Record<
  string,
  { label: string; icon: LucideIcon; badgeColor: string }
> = {
  individual_tutoring: {
    label: 'Tutorat individuel',
    icon: User,
    badgeColor: 'var(--color-5-nuance-3)',
  },
  information_meeting: {
    label: "Réunion d'information",
    icon: Info,
    badgeColor: 'var(--color-2-nuance-1)',
  },
  welcome_meeting: {
    label: "Réunion d'accueil",
    icon: Users,
    badgeColor: 'var(--color-3-nuance-1)',
  },
  workshop: {
    label: 'Événement terrain',
    icon: UsersRound,
    badgeColor: 'var(--color-4-nuance-1)',
  },
};

/** Badge + icon fallback for a remote report participant role. */
export interface RemoteReportParticipantRoleConfig {
  label: string;
  icon: LucideIcon;
  badgeColor: string;
  badgeVariant: 'ghost' | 'outlined';
}

/**
 * Static fallback config for participant roles in remote reports.
 * Used when the backend doesn't return roleLabel/roleIcon/roleColor.
 */
function participantRoleFallback(
  code: string,
  icon: LucideIcon,
  badgeColor: string,
): RemoteReportParticipantRoleConfig {
  return {
    get label() {
      return getRoleLabel(code);
    },
    icon,
    badgeColor,
    badgeVariant: 'ghost',
  };
}

/** Fallback role badge when the API omits roleLabel / roleIcon / roleColor. */
export const REMOTE_REPORT_PARTICIPANT_ROLE_CONFIG: Record<
  string,
  RemoteReportParticipantRoleConfig
> = {
  tutor: participantRoleFallback('tutor', GraduationCap, 'var(--color-1-nuance-1)'),
  tutor_remote: participantRoleFallback('tutor_remote', GraduationCap, 'var(--color-1-nuance-1)'),
  student: participantRoleFallback('student', User, 'var(--color-2-nuance-1)'),
  student_remote: participantRoleFallback('student_remote', User, 'var(--color-2-nuance-1)'),
  parent: participantRoleFallback('parent', Heart, 'var(--color-3-nuance-1)'),
  parent_remote: participantRoleFallback('parent_remote', Heart, 'var(--color-3-nuance-1)'),
  volunteer: participantRoleFallback('volunteer', Handshake, 'var(--color-2-nuance-2)'),
  recruiter_remote: participantRoleFallback(
    'recruiter_remote',
    Briefcase,
    'var(--color-2-nuance-1)',
  ),
  coordinator_remote: participantRoleFallback(
    'coordinator_remote',
    UserCog,
    'var(--color-1-nuance-2)',
  ),
  animator: participantRoleFallback('animator', Shield, 'var(--color-3-nuance-1)'),
};

import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';

/** Active filter state for the remote report list. */
export interface ReportRemoteFilters {
  reportStatus: string[];
  scoringStatus: string[];
  sessionTypes: string[];
  dateRange: 'all' | 'today' | 'week' | 'month' | 'year';
  customDateFrom?: Date;
  customDateTo?: Date;
  search?: string;
  tutorUserId?: string;
  studentUserId?: string;
  animatorUserId?: string;
  programId?: string;
  schoolYearId?: string;
  managedByIds?: ManagedByValue;
}

/** Empty remote report list filters. */
export const DEFAULT_REPORT_REMOTE_FILTERS: ReportRemoteFilters = {
  reportStatus: [],
  scoringStatus: [],
  sessionTypes: ['individual_tutoring'],
  dateRange: 'all',
  customDateFrom: undefined,
  customDateTo: undefined,
  search: undefined,
  tutorUserId: undefined,
  studentUserId: undefined,
};

/** Filter group ids for the remote reports configure panel. */
export type ReportRemoteFilterGroupId =
  | 'filter-search'
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-status-report'
  | 'filter-status-scoring'
  | 'filter-tutor'
  | 'filter-student'
  | 'filter-managed-by'
  | 'filter-program'
  | 'filter-school-year';

/** Filter group ids shown in the remote configure panel. */
export const ALL_REPORT_REMOTE_FILTER_GROUP_IDS: ReportRemoteFilterGroupId[] = [
  'filter-search',
  'filter-period',
  'filter-date-range',
  'filter-status-report',
  'filter-status-scoring',
  'filter-tutor',
  'filter-student',
  'filter-managed-by',
  'filter-program',
  'filter-school-year',
];

/** Human-readable label per remote filter group. */
export const REPORT_REMOTE_FILTER_GROUP_LABELS: Record<ReportRemoteFilterGroupId, string> = {
  'filter-search': 'Recherche',
  'filter-period': 'Période',
  'filter-date-range': 'Dates',
  'filter-status-report': 'Statut rapport',
  'filter-status-scoring': 'Statut Scoring',
  get 'filter-tutor'() {
    return getRoleLabel('tutor');
  },
  get 'filter-student'() {
    return getRoleLabel('student');
  },
  'filter-managed-by': 'Géré par',
  'filter-program': 'Programme',
  'filter-school-year': 'Année scolaire',
};

/**
 * Maps a session type key to its {@link ReportTab} category.
 * Derived from {@link REMOTE_REPORT_TAB_SESSION_TYPES} (reverse mapping).
 */
export const REPORT_SESSION_TYPE_TO_TAB: Record<string, ReportTab> = Object.fromEntries(
  (Object.entries(REMOTE_REPORT_TAB_SESSION_TYPES) as [ReportTab, string[]][])
    .filter(([tab]) => tab !== 'all')
    .flatMap(([tab, types]) => types.map((type) => [type, tab])),
);

/** Search placeholder per remote report tab. */
export const REPORT_REMOTE_SEARCH_PLACEHOLDERS: Record<ReportTab, string> = {
  tutoring: 'Nom tuteur ou élève...',
  'information-meeting': 'Nom animateur...',
  'welcome-meeting': 'Nom animateur...',
  'field-event': 'Nom animateur...',
  all: 'Recherche globale...',
};

/**
 * Session types whose report workflow uses incomplet/complet (synthesis schema)
 * instead of a_saisir/saisi/validé (tutoring schema).
 */
export const SYNTHESIS_SESSION_TYPES = new Set(['information_meeting', 'welcome_meeting']);

/** Maps scoring targetRole keys to display labels. */
export const SCORING_TARGET_LABELS: Record<string, string> = {
  get student() {
    return getRoleLabel('student');
  },
  get tutor_participant() {
    return `${getRoleLabel('tutor')} participant`;
  },
};

/** Tab frontend → schema backend used by create/edit forms. */
export const REPORT_REMOTE_SCHEMA = {
  tutoring: 'cr_tutorat',
  synthesisInformationMeeting: 'synthese_ri',
  synthesisWelcomeMeeting: 'synthese_ra',
} as const;

/** Backend schema id used by remote create/edit forms. */
export type RemoteSchemaId = (typeof REPORT_REMOTE_SCHEMA)[keyof typeof REPORT_REMOTE_SCHEMA];

/** Tab frontend → schema backend used by create/edit forms. */
export const REPORT_REMOTE_TAB_TO_SCHEMA: Record<Exclude<ReportTab, 'all'>, RemoteSchemaId> = {
  tutoring: REPORT_REMOTE_SCHEMA.tutoring,
  'information-meeting': REPORT_REMOTE_SCHEMA.synthesisInformationMeeting,
  'welcome-meeting': REPORT_REMOTE_SCHEMA.synthesisWelcomeMeeting,
  'field-event': REPORT_REMOTE_SCHEMA.synthesisWelcomeMeeting,
};

/** Scoring schema backend used by create/edit forms. */
export const REPORT_REMOTE_SCORING_SCHEMA = {
  student: 'scoring_eleve',
  lightRi: 'scoring_light_ri',
  lightRa: 'scoring_light_ra',
} as const;

/**
 * Role groups for filtering participants in remote reports.
 * Derived from the keys of {@link REMOTE_REPORT_PARTICIPANT_ROLE_CONFIG}.
 */
export const REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS = {
  tutor: ['tutor', 'tutor_remote'] as const,
  student: ['student', 'student_remote'] as const,
  parent: ['parent', 'parent_remote'] as const,
} as const;

/** All known/classified participant roles (used to derive the "others" group). */
export const REPORT_REMOTE_KNOWN_PARTICIPANT_ROLES: readonly string[] = [
  ...REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS.tutor,
  ...REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS.student,
  ...REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS.parent,
];

/** Meeting type label used in {@link SynthesisView} subtitle. */
export const SYNTHESIS_MEETING_TYPE_LABEL: Record<string, string> = {
  synthese_ri: "d'information",
  synthese_ra: "d'accueil",
};

/** Detailed evaluation fields for tutoring reports. */
export const TUTORING_REPORT_EVALUATION_FIELDS = [
  { key: 'attention' as const, label: "Attention de l'élève" },
  { key: 'engagement' as const, label: 'Engagement' },
  { key: 'participation' as const, label: 'Participation' },
  { key: 'comprehension' as const, label: 'Compréhension' },
];

/** Skills fields for tutoring reports. */
export const TUTORING_REPORT_SKILLS_FIELDS = [
  { key: 'concentration' as const, label: 'Concentration' },
  { key: 'motivation' as const, label: 'Motivation' },
  { key: 'autonomie' as const, label: 'Autonomie' },
];

/** Scoring criteria fields for scoring_eleve. */
export const SCORING_STUDENT_CRITERIA_FIELDS = [
  { key: 'comportementTuteur' as const, label: 'Comportement du tuteur' },
  { key: 'clarteExplications' as const, label: 'Clarté des explications' },
  { key: 'utiliteExercices' as const, label: 'Utilité des exercices' },
];

/** UI labels for {@link SessionReportFeeling}. */
export const SESSION_REPORT_FEELING_LABELS: Record<SessionReportFeeling, string> = {
  very_at_ease: "Très à l'aise",
  rather_at_ease: "Plutôt à l'aise",
  neutral: 'Neutre',
  somewhat_difficult: 'Un peu en difficulté',
  difficult: 'En difficulté',
};

/** Select options built from {@link SESSION_REPORT_FEELING_VALUES}. */
export const SESSION_REPORT_FEELING_OPTIONS: { value: SessionReportFeeling; label: string }[] =
  SESSION_REPORT_FEELING_VALUES.map((value) => ({
    value,
    label: SESSION_REPORT_FEELING_LABELS[value],
  }));
